import React, { useState, useRef } from 'react'

import { Popper, ClickAwayListener, Button, InputAdornment } from '@material-ui/core'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'

import { CustomOutlinedInput } from '../BPMTable/BPMComponents'
import { formatPeriod, periodFromDate } from '../../utils/DateUtils'
import { periodSelectionFrom } from './PeriodSelection'
import PeriodGrid from './PeriodGrid'
import usePeriodBounds from './usePeriodBounds'

const calendarIconStyle = {
    color: 'white',
    position: 'absolute',
    justifyContent: 'none',
    left: 2,
}

/**
 * Period cell for a BPM table filter row.
 *
 * The committed filter value is the only source of truth for what is displayed,
 * so the cell always agrees with the query that is actually running — including
 * after a remount, since filters are persisted per company and table.
 *
 * Props:
 *   name, filterId  filter key; `name` is echoed back on the change event
 *   filters         the table's current filter map
 *   onChangeDate    change handler, called with a synthetic `{target}` event
 *   bounds          optional PERIOD_BOUNDS source
 */
const PeriodFilterPicker = (props) => {
    const { name, filterId, filters, onChangeDate, bounds: boundsSource } = props
    const bounds = usePeriodBounds(boundsSource)

    const [anchorEl, setAnchorEl] = useState(null)
    // Which year the grid is showing. View state only, reset on every open.
    const [browsedYear, setBrowsedYear] = useState(null)
    const input = useRef(null)

    const selection = periodSelectionFrom(filters[filterId])
    const browsingYear = browsedYear || selection.yearOr(bounds.defaultYear(periodFromDate(new Date()).year))

    const emit = (value) => {
        onChangeDate({ target: { name, value } })
    }

    const handleOpen = (event) => {
        setBrowsedYear(null)
        setAnchorEl(event.currentTarget)
    }

    const handleSelect = (year, month) => {
        setAnchorEl(null)
        emit(formatPeriod({ year, month }))
    }

    const handleClear = () => {
        setAnchorEl(null)
        emit('')
    }

    return (
        <>
            <CustomOutlinedInput
                ref={input}
                style={{ width: '100%', fontSize: 15 }}
                disabled={true}
                startAdornment={
                    <InputAdornment position='start'>
                        <Button size='small' style={calendarIconStyle} onClick={handleOpen}>
                            <CalendarTodayIcon fontSize='small' />
                            {selection.label()}
                        </Button>
                    </InputAdornment>
                }
            />
            <Popper
                anchorEl={input.current}
                open={Boolean(anchorEl)}
                popperOptions={{ onCreate: (data) => data.instance.popper.style.zIndex = 1 }}
            >
                <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                    <div>
                        <PeriodGrid
                            year={browsingYear}
                            bounds={bounds}
                            onChangeYear={setBrowsedYear}
                            onSelect={handleSelect}
                            onClear={selection.clearActionOr(handleClear)}
                        />
                    </div>
                </ClickAwayListener>
            </Popper>
        </>
    )
}

export default PeriodFilterPicker
